import { useNavigate, useParams } from "react-router";
import { AuthContext, useAuth } from "../../utility/useAuth";
import { StyledJobImagesPage } from "./JobImagesPage.styles";
import { useEffect, useState } from "react";
import { IJobDetails } from "../../types/IJobDetails";
import {
  deleteDocument,
  getDocumentContent,
  getDocuments,
  getJobDetails,
  uploadDocument,
} from "../../services/ApiService";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import { PartOrderWithImages } from "../../types/PartOrderWithImages";
import { Col, Container, Row } from "react-grid-system";
import {
  MenuComponent,
  MenuEventArgs,
  MenuItemModel,
} from "@syncfusion/ej2-react-navigations";
import { useStoreActions } from "../../store";
import { HeaderMode } from "../../types/HeaderMode";
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Resize,
  RowSelectEventArgs,
  SelectionSettingsModel,
} from "@syncfusion/ej2-react-grids";
import {
  ListBoxChangeEventArgs,
  ListBoxComponent,
} from "@syncfusion/ej2-react-dropdowns";
import { getPathItem } from "../../utility/pathHelper";
import { PathItem } from "../../types/PathItem";
import { DeleteImageModal } from "../../components/Modals/DeleteImageModal/DeleteImageModal";
import { AddImageModal } from "../../components/Modals/AddImageModal/AddImageModal";
import { CaptureImageModal } from "../../components/Modals/CaptureImageModal/CaptureImageModal";
import { ViewImageModal } from "../../components/Modals/ViewImageModal/ViewImageModal";
import { getUniqueFileName } from "../../utility/partOrderHelper";
import { FileContent } from "use-file-picker/dist/interfaces";
import { useFilePicker } from "use-file-picker";

export const JobImagesPage: React.FC = () => {
  let menuObj: MenuComponent | null;
  let listboxObj: ListBoxComponent | null;
  const { jobNumber } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const { appSettings: action } = useStoreActions((actions) => actions);

  const [jobDetails, setJobDetails] = useState<IJobDetails>();
  const [partsOrders, setPartOrders] = useState<PartOrderWithImages[]>([]);
  const [selectedPartsOrder, setSelectedPartOrder] =
    useState<PartOrderWithImages>();
  const [selectedImages, setSelectedImages] = useState<PathItem[]>([]);
  const [selectedImage, setSelectedImage] = useState<PathItem>();
  const [showDeleteImageModal, setShowDeleteImageModal] = useState(false);
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [showCaptureImageModal, setShowCaptureImageModal] = useState(false);
  const [showViewImageModal, setShowViewImageModal] = useState(false);

  const { openFilePicker, filesContent, loading } = useFilePicker({
    readAs: "ArrayBuffer",
    accept: "image/*",
    multiple: true,
    readFilesContent: true,
    onFilesSuccessfullySelected: ({ plainFiles, filesContent }) => {
      // this callback is called when there were no validation errors
      console.log("onFilesSuccessfullySelected", plainFiles, filesContent);
      uploadFiles(`images${selectedPartsOrder?.weldId}`, filesContent)
        .then(() => {
          if (jobDetails) {
            loadPartOrders(jobDetails).then((orders) => {
              const order = orders.find(
                (o) => o.weldId === selectedPartsOrder?.weldId,
              );
              setSelectedPartOrder(order);
              const images = order?.images.map((image) => getPathItem(image));
              if (images) {
                setSelectedImages(images);
              }
            });
          }
        })
        .catch((error) => {
          handleError(error, "Failed to upload images(s).");
        });
    },
  });

  async function uploadFiles(
    folder: string,
    files: FileContent<ArrayBuffer>[],
  ): Promise<void> {
    for (const file of files) {
      await uploadDocument(auth, folder, file.name, new Blob([file.content]));
    }
  }

  async function loadPartOrders(
    jobDetails: IJobDetails,
  ): Promise<PartOrderWithImages[]> {
    const documents = await getDocuments(auth, "images");

    let orders: PartOrderWithImages[] = [];
    for (let i = 1; i <= jobDetails.qtyOrdered; i++) {
      const weld = `${jobNumber}-${i}`;
      const partOrderImages = documents.filter((d) =>
        d.startsWith(`images${weld}`),
      );

      orders.push({
        weldId: weld,
        imageCount: partOrderImages.length,
        images: partOrderImages,
      });
    }

    setPartOrders(orders);
    return orders;
  }

  useEffect(() => {
    const fetchData = async (jobNumber: string) => {
      // get the data from the api
      const jobDetails = await getJobDetails(auth, jobNumber);

      setJobDetails(jobDetails);

      await loadPartOrders(jobDetails);
    };
    if (jobNumber) {
      fetchData(jobNumber)
        // make sure to catch any error
        .catch((error) => {
          const message = `Failed to get job details. Error: ${error}`;
          console.error(`Failed to get job details. Error: ${error}`);
          ToastUtility.show(message, "Error", 2000);
        });
    }

    if (jobNumber) {
      action.setJobNumber(jobNumber);
      action.setHeaderMode(HeaderMode.ProductionOrderImages);
      getJobDetails(auth, jobNumber)
        .then((data) => {
          setJobDetails(data);
        })
        .catch((error) => {
          const message = `Failed to get job details. Error: ${error}`;
          console.error(`Failed to get job details. Error: ${error}`);
          ToastUtility.show(message, "Error", 2000);
        });
    }
  }, [jobNumber]);

  const menuItems: MenuItemModel[] = [
    { id: "back", text: "Back", iconCss: "e-icons e-back" },
    { id: "addImages", text: "Add", iconCss: "e-icons e-folder-open" },
    { id: "captureImage", text: "Capture", iconCss: "e-icons e-video" },
    { id: "deleteImage", text: "Delete", iconCss: "e-icons e-trash" },
    {
      id: "downloadImage",
      text: "Download",
      iconCss: "e-icons e-align-bottom",
    },
  ];

  const toolTips = new Map<string, string>([
    ["addImages", "Add Images"],
    ["captureImage", "Capture Image"],
    ["deleteImage", "Delete Image"],
    ["downloadImage", "Download Image"],
  ]);

  let disableItems: string[] = ["Delete", "Download"];

  function beforeItemRender(args: MenuEventArgs): void {
    args.element.setAttribute("title", toolTips.get(args.item.id ?? "") ?? "");
  }

  function created(): void {
    // Disable items
    if (menuObj) {
      menuObj.enableItems(["Add", "Capture"], false, false);
      menuObj.enableItems(["Delete", "Download"], false, false);
    }
  }

  function selectMenuItem(args: MenuEventArgs): void {
    const id = args.item.id;

    if (id === "deleteImage") {
      setShowDeleteImageModal(true);
    } else if (id === "addImages") {
      openFilePicker();
      //setShowAddImageModal(true);
    } else if (id === "captureImage") {
      setShowCaptureImageModal(true);
    } else if (id === "downloadImage" && selectedImage) {
      getDocumentContent(auth, selectedImage.folder, selectedImage.fileName)
        .then((data) => {
          if (data) {
            const href = URL.createObjectURL(data);
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", selectedImage.fileName); //or any other extension
            document.body.appendChild(link);
            link.click();

            // clean up "a" element & remove ObjectURL
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
          }
        })
        .catch((error) => {
          const message = `Failed to get image content. Error: ${error}`;
          console.error(message);
          //ToastUtility.show(message, "Error", 2000);
          return;
        });
    } else if (id === "back") {
      navigate(-1);
    }
  }

  function onPartsOrderSelected(args: RowSelectEventArgs) {
    if (menuObj) {
      menuObj.enableItems(disableItems, false, false);
      menuObj.enableItems(["Add", "Capture"], false, false);
    }
    const partOrder = args.data as PartOrderWithImages;
    if (partOrder) {
      setSelectedPartOrder(partOrder);
      const images = partOrder.images.map((image) => getPathItem(image));
      if (images) {
        setSelectedImages(images);
      }

      if (menuObj) {
        menuObj.enableItems(["Add", "Capture"], true, false);
      }
    }
  }

  function onImageSelected(args: ListBoxChangeEventArgs) {
    if (menuObj) {
      menuObj.enableItems(disableItems, false, false);
    }
    if (args.items && args.items.length === 1) {
      setSelectedImage(args.items[0] as PathItem);
      if (menuObj) {
        menuObj.enableItems(disableItems, true, false);
      }
    }
  }

  const selectionSettings: SelectionSettingsModel = {
    mode: "Row",
    type: "Single",
  };

  function Template(data: any): JSX.Element {
    const [img, setImg] = useState<string>();

    const fetchImage = async () => {
      const res = await getDocumentContent(
        auth,
        data.folder,
        data.fileName,
        true,
      ).catch((error) => {
        const message = `Failed to get image content. Error: ${error}`;
        console.error(message);
        //ToastUtility.show(message, "Error", 2000);
        return;
      });
      if (res) {
        const imageObjectURL = URL.createObjectURL(res);
        setImg(imageObjectURL);
      }
    };

    useEffect(() => {
      fetchImage();
    }, []);

    return (
      //<div className="list-wrapper" style={{ width: 400 }}>
      <Container
        fluid
        style={{ margin: 10 }}
        onDoubleClick={() => {
          const image = selectedImages.find(
            (i) => i.fileName === data.fileName,
          );
          setSelectedImage(image);
          setShowViewImageModal(true);
        }}
      >
        <Row align="stretch">
          <span>{`${data.fileName}`}</span>
        </Row>
        <Row align="stretch">
          <img height={120} width={120} src={img} />
        </Row>
      </Container>
      //</div>
    );
  }

  function handleError(error: any, prefix?: string) {
    const message = `${prefix} Error: ${error}`;
    console.error(message);
    ToastUtility.show(message, "Error", 2000);
  }

  function onDeleteImage(document: PathItem) {
    setShowDeleteImageModal(false);
    const partOrder = selectedPartsOrder;
    setSelectedPartOrder(undefined);
    setSelectedImages([]);
    deleteDocument(auth, document.folder, document.fileName)
      .then(() => {
        if (jobDetails) {
          loadPartOrders(jobDetails).then((orders) => {
            const order = orders.find((o) => o.weldId === partOrder?.weldId);
            setSelectedPartOrder(order);
            const images = order?.images.map((image) => getPathItem(image));
            if (images) {
              setSelectedImages(images);
            }
          });
        }
      })
      .catch((error) => {
        handleError(error, "Failed to delete image.");
      });
  }

  function onAddImage(folder: string, imageName: string, imageContent: Blob) {
    setShowAddImageModal(false);
    setShowCaptureImageModal(false);
    const partOrder = selectedPartsOrder;
    setSelectedPartOrder(undefined);
    setSelectedImages([]);
    if (folder?.length > 0 && imageName?.length > 0 && imageContent) {
      uploadDocument(auth, folder, imageName, imageContent)
        .then(() => {
          if (jobDetails) {
            loadPartOrders(jobDetails).then((orders) => {
              const order = orders.find((o) => o.weldId === partOrder?.weldId);
              setSelectedPartOrder(order);
              const images = order?.images.map((image) => getPathItem(image));
              if (images) {
                setSelectedImages(images);
              }
            });
          }
        })
        .catch((error) => {
          handleError(error, "Failed to upload image.");
        });
    }
  }

  return (
    <StyledJobImagesPage>
      {partsOrders ? (
        <Container fluid>
          <Row align="stretch">
            <MenuComponent
              ref={(scope : MenuComponent) => (menuObj = scope)}
              items={menuItems}
              select={selectMenuItem}
              beforeItemRender={beforeItemRender}
              created={created}
            />
          </Row>
          <Row align="stretch">
            <Col sm={6}>
              <GridComponent
                id="Grid"
                dataSource={partsOrders}
                selectionSettings={selectionSettings}
                //selectedRowIndex={0}
                allowResizing
                height={600}
                gridLines="Both"
                rowSelected={onPartsOrderSelected}
              >
                <Inject services={[Resize]} />
                <ColumnsDirective>
                  <ColumnDirective
                    field="weldId"
                    isPrimaryKey={true}
                    headerText="Part Production Order"
                    width="150"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="imageCount"
                    isPrimaryKey={true}
                    headerText="Image Count"
                    width="100"
                    textAlign="Left"
                  />
                </ColumnsDirective>
              </GridComponent>
            </Col>
            {selectedPartsOrder && (
              <Col>
                <ListBoxComponent
                  id="listbox"
                  ref={(scope: ListBoxComponent) => (listboxObj = scope)}
                  height={650}
                  dataSource={selectedImages}
                  selectionSettings={{ mode: "Single" }}
                  itemTemplate={Template}
                  change={onImageSelected}
                  fields={{ text: "fileName" }}
                />
              </Col>
            )}
          </Row>
        </Container>
      ) : (
        <>Loading...</>
      )}
      {showDeleteImageModal && selectedImage && (
        <DeleteImageModal
          open={showDeleteImageModal}
          document={selectedImage}
          onDeleteImage={onDeleteImage}
          onClose={() => {
            setShowDeleteImageModal(false);
          }}
        />
      )}
      {showAddImageModal && selectedPartsOrder && (
        <AddImageModal
          open={showAddImageModal}
          partProductionOrder={selectedPartsOrder?.weldId}
          onClose={() => {
            setShowAddImageModal(false);
          }}
          onAddImage={onAddImage}
        />
      )}
      {showCaptureImageModal && selectedPartsOrder && (
        <CaptureImageModal
          open={showCaptureImageModal}
          folder={selectedPartsOrder?.weldId}
          initImageName={getUniqueFileName(selectedPartsOrder)}
          onClose={() => {
            setShowCaptureImageModal(false);
          }}
          onAddImage={onAddImage}
        />
      )}
      {showViewImageModal && selectedPartsOrder && selectedImage?.fileName && (
        <ViewImageModal
          open={showViewImageModal}
          partProductionOrder={selectedPartsOrder?.weldId}
          imageName={selectedImage.fileName}
          onClose={() => {
            setShowViewImageModal(false);
          }}
        />
      )}
    </StyledJobImagesPage>
  );
};
